import { Badge, BreadcrumbProps, Button, Card, CardBody, CodeDisplay, DataTable, FilterPanel, Icon, Skeleton, Spinner, Tooltip, useToast } from '@aidkitorg/component-library'
import { ReportFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSpinDelay } from 'spin-delay'
import { ReportData, ReportWithConfig, useReports } from '../../Contexts/ReportsContext'
import { useSettings } from '../../Contexts/SettingsContext'
import { useLocalizedStrings } from '../../Localization'
import { AdminPanelPage } from '../AdminPanelPage'
import { FilterProvider, useFilter, useFilterPanel, useSettingsFilterFields } from '../Filters'
import { hasAnyFilters } from '../shared/utils/filters'
import { renderViewApplicantsCell, SYNTHETIC_UID_COLUMN_NAME, SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME } from './utils/reportColumns'
import { ReportExportModal } from './components/ReportExportModal'
import { useFieldValueSuggestions } from '../shared/hooks/useFieldValueSuggestions';

type ReportColumnData = {
  key: string;
  label: string;
}

// Empty State Components
const LoadingDataState: React.FC<{ message: string }> = React.memo(({ message }) => (
  <div className="flex flex-col gap-2 items-center justify-center h-96 text-gray-600">
    <Spinner />
    <span>{message}</span>
  </div>
))

const EmptyDataState: React.FC<{ message: string }> = React.memo(({ message }) => (
  <div className="flex items-center justify-center h-64 text-gray-500">
    <div className="text-center">
      <div className="text-lg font-medium mb-2">{message}</div>
    </div>
  </div>
))


const ReportViewPageInner = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
  const location = useLocation()
  const { reportId } = useParams<{ reportId: string }>()
  const { toast, dismiss } = useToast()
  const { fetchReportById, getReportById, generateReportData, generateReportDataImmediate, isExporting, exportJobs } = useReports()
  const { getFields } = useSettings()
  const allFields = useSettingsFilterFields()
  const { filterConfig: viewFilters, setFilterConfig: setViewFilters, setAvailableFields } = useFilter()
  const { filterPanelProps, filterRef } = useFilterPanel(`report-view-${reportId}`)
  const { fetchSuggestions } = useFieldValueSuggestions();

  const [report, setReport] = useState<ReportWithConfig | null>(null)
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [reportFetched, setReportFetched] = useState(false)
  const [hasDataLoadingStarted, setHasDataLoadingStarted] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isForceRefreshing, setIsForceRefreshing] = useState(false)
  const lastApiCallFiltersRef = useRef<string>('');
  const [exportModalOpen, setExportModalOpen] = useState(false)
  // Use refs to track export state to avoid race conditions with useEffect
  const exportStateRef = useRef<{
    toastId: string | null;
    toastForTaskId: string | null;
    downloadedTaskId: string | null;
  }>({ toastId: null, toastForTaskId: null, downloadedTaskId: null })

  // Table expand/collapse state with URL persistence
  const [isTableExpanded, setIsTableExpanded] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'expanded';
  });

  // Use ref to track force refresh state for proper cleanup
  const isForceRefreshingRef = useRef(isForceRefreshing);
  isForceRefreshingRef.current = isForceRefreshing;

  // URL parameter helpers for column visibility
  const getColumnsFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const orderParam = params.get('columns');
    const hiddenParam = params.get('hidden');

    return {
      order: orderParam ? orderParam.split(',').filter(Boolean) : null,
      hidden: hiddenParam ? hiddenParam.split(',').filter(Boolean) : []
    };
  }, [location.search]);

  const updateUrlWithColumns = useCallback((columnOrder: string[], hiddenColumns: string[]) => {
    const params = new URLSearchParams(location.search);

    // Only set columns param if order has been customized and has items
    if (columnOrder.length > 0) {
      params.set('columns', columnOrder.join(','));
    } else {
      params.delete('columns');
    }

    // Only set hidden param if there are hidden columns
    if (hiddenColumns.length > 0) {
      params.set('hidden', hiddenColumns.join(','));
    } else {
      params.delete('hidden');
    }

    const newUrl = `${location.pathname}?${params.toString()}`;
    history.replace(newUrl);
  }, [location.search, location.pathname, history]);

  // Handle expand/collapse toggle with URL persistence
  const handleExpandToggle = useCallback(() => {
    setIsTableExpanded(prevState => {
      const newExpandedState = !prevState;

      const params = new URLSearchParams(location.search);
      if (newExpandedState) {
        params.set('view', 'expanded');
      } else {
        params.delete('view');
      }

      const newUrl = `${location.pathname}?${params.toString()}`;
      history.replace(newUrl);

      return newExpandedState;
    });
  }, [location.search, location.pathname, history]);

  // Cache for recent API responses
  const responseCache = useRef<Map<string, { data: ReportData; timestamp: number }>>(new Map());
  const CACHE_DURATION = 3 * 60 * 1000; // 3 minutes

  // Helper function to check and use cache
  const getCachedData = useCallback((cacheKey: string) => {
    const cached = responseCache.current.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, [CACHE_DURATION]);

  // Helper function to store data in cache
  const setCachedData = useCallback((cacheKey: string, data: ReportData) => {
    responseCache.current.set(cacheKey, { data, timestamp: Date.now() });

    // Clean up old cache entries
    const now = Date.now();
    for (const [key, value] of Array.from(responseCache.current.entries())) {
      if (now - value.timestamp > CACHE_DURATION) {
        responseCache.current.delete(key);
      }
    }
  }, [CACHE_DURATION]);

  const showDataLoadingSpinner = useSpinDelay(isDataLoading, { delay: 250, minDuration: 250 })

  // Generate available fields for filtering based on report config columns and field definitions
  const availableFieldsForFilters = useMemo(() => {
    if (!report?.config?.columns) {
      return []
    }

    // Extract field names from report config columns
    const configuredFieldNames = new Set<string>()
    report.config.columns.forEach(col => {
      if (col.kind === 'Field') {
        // Handle synthetic UID field
        if (col.field === '__synthetic_uid__') {
          configuredFieldNames.add('uid')
        }
        // Skip synthetic view applicants field - it's UI-only
        else if (col.field === '__synthetic_view_applicants__') {
          // Don't add to configuredFieldNames
        } else {
          configuredFieldNames.add(col.field)
        }
      }
    })

    // If fields are not loaded yet, return basic field info from config
    if (!allFields || allFields.length === 0) {
      const basicFields = Array.from(configuredFieldNames).map(fieldName => ({
        key: fieldName,
        label: fieldName === 'uid' ? 'Applicant UID' : fieldName.replace(/_/g, ' ').replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
        type: 'text' as const
      }))
      return basicFields
    }

    // Filter all fields to only those in the report columns
    const availableFields = allFields.filter(field => configuredFieldNames.has(field.key))

    // Add UID field if it was configured and not already in the list
    if (configuredFieldNames.has('uid')) {
      const hasUidField = availableFields.some(field => field.key === 'uid')
      if (!hasUidField) {
        availableFields.unshift({
          key: 'uid',
          label: 'Applicant UID',
          type: 'text' as const
        })
      }
    }

    return availableFields
  }, [report?.config?.columns, allFields])

  // Update context with available fields when they change
  useEffect(() => {
    setAvailableFields(availableFieldsForFilters)
  }, [availableFieldsForFilters, setAvailableFields])

  // Handle export completion - show toast and trigger download
  const exportJob = reportId ? exportJobs[reportId] : undefined;

  const triggerDownload = useCallback((url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (!exportJob) return;

    const state = exportStateRef.current;

    const downloadUrl = exportJob.result?.data?.downloadUrl;
    const filename = exportJob.result?.data?.filename;
    const errorMessage = exportJob.result?.error?.message || exportJob.result?.message;

    // Show "Preparing" toast when export starts (only if we haven't shown it yet for this task)
    if ((exportJob.status === 'QUEUED' || exportJob.status === 'STARTED') &&
        state.toastForTaskId !== exportJob.taskId) {
      // Dismiss any previous toast
      if (state.toastId) {
        dismiss(state.toastId);
      }

      const { id } = toast({
        title: L.admin_panel.reports.export_preparing,
        description: L.admin_panel.reports.export_preparing_desc,
        variant: 'default',
        autoClose: false,
      });
      state.toastId = id;
      state.toastForTaskId = exportJob.taskId;
    }

    // Handle completed export - only process once per taskId
    if (exportJob.status === 'FINISHED' && downloadUrl && state.downloadedTaskId !== exportJob.taskId) {
      state.downloadedTaskId = exportJob.taskId;

      // Dismiss the "preparing" toast if it exists
      if (state.toastId) {
        dismiss(state.toastId);
        state.toastId = null;
        state.toastForTaskId = null;
      }

      // Show success toast (auto-dismisses after 3 seconds)
      toast({
        title: L.admin_panel.reports.export_ready,
        description: L.admin_panel.reports.export_ready_desc,
        variant: 'success',
        duration: 3000,
      });

      // Trigger download
      triggerDownload(downloadUrl, filename || 'export.zip');
    } else if (exportJob.status === 'FAILED' && state.downloadedTaskId !== exportJob.taskId) {
      state.downloadedTaskId = exportJob.taskId;

      // Dismiss the "preparing" toast if it exists
      if (state.toastId) {
        dismiss(state.toastId);
        state.toastId = null;
        state.toastForTaskId = null;
      }

      // Show error toast (stays until dismissed)
      toast({
        title: L.admin_panel.reports.export_failed,
        description: errorMessage || L.admin_panel.reports.export_failed_desc,
        variant: 'error',
        autoClose: false,
      });
    }
  }, [exportJob, toast, dismiss, triggerDownload]);

  // Handle view filter changes
  const handleViewFilterChange = useCallback((newFilterConfig: ReportFilterConfig) => {
    // Only process filters if they're disabled or have complete conditions
    const hasComplete = hasAnyFilters(newFilterConfig);
    const normalizedFilters = (newFilterConfig.enabled && hasComplete) ? newFilterConfig : undefined;

    // Reset page to 1 when filters change
    setCurrentPage(1);
    setViewFilters(normalizedFilters);
  }, [setViewFilters, hasAnyFilters])


  // Clear session history when page loads
  useEffect(() => {
    if (reportId) {
      const sessionKey = `reports-filter-report-view-${reportId}`;
      sessionStorage.removeItem(sessionKey);
    }
  }, [reportId]);

  // Load fields for filters
  useEffect(() => {
    getFields();
  }, [getFields]);

  // Fetch report metadata on mount
  useEffect(() => {
    const loadReport = async () => {
      if (!reportId || reportFetched) return

      setIsLoading(true)
      try {
        await fetchReportById(reportId)
        setReportFetched(true)
      } catch (error) {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report,
          variant: 'error'
        })
        history.push('/admin-panel/reports')
      } finally {
        setIsLoading(false)
      }
    }

    loadReport()
  }, [reportId, reportFetched])

  // Update local report state when context updates
  useEffect(() => {
    if (reportFetched && reportId) {
      const fetchedReport = getReportById(reportId)
      if (fetchedReport) {
        setReport(fetchedReport)
      } else if (!isLoading) {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.report_not_found,
          variant: 'error'
        })
        history.push('/admin-panel/reports')
      }
    }
  }, [reportFetched, reportId, isLoading])

  // Generate report data when report config is loaded, pagination changes, or view filters change
  useEffect(() => {
    const loadReportData = async () => {
      if (!report?.config) return

      // Skip API call if we're about to make the same call with identical filters, pagination, and sorting
      const currentCallString = JSON.stringify({
        filters: viewFilters,
        page: currentPage,
        pageSize: pageSize,
        sorting: sorting
      });
      if (lastApiCallFiltersRef.current === currentCallString) {
        return;
      }

      // Capture previous call BEFORE updating the ref
      const previousCallString = lastApiCallFiltersRef.current;

      // Update the ref immediately to prevent infinite loops
      lastApiCallFiltersRef.current = currentCallString;

      // Check cache first (but skip cache for initial load and force refresh)
      if (!isInitialLoad) {
        const cachedData = getCachedData(currentCallString);
        if (cachedData) {
          setReportData(cachedData);
          setHasDataLoadingStarted(true);
          return;
        }
      }

      // Mark that data loading has started (for initial load detection)
      setHasDataLoadingStarted(true);
      setIsDataLoading(true);

      try {
        // Use immediate version for initial load, pagination, sorting changes
        // Use debounced version only for pure filter changes
        let useImmediate = isInitialLoad;

        if (!isInitialLoad && previousCallString) {
          try {
            const prevCallObj = JSON.parse(previousCallString);
            const currentCallObj = JSON.parse(currentCallString);

            // If page, pageSize, sorting changed, or filters were cleared, use immediate
            const paginationChanged = prevCallObj.page !== currentCallObj.page || prevCallObj.pageSize !== currentCallObj.pageSize;
            const sortingChanged = JSON.stringify(prevCallObj.sorting) !== JSON.stringify(currentCallObj.sorting);

            // Check if filters were cleared (had filters before, now don't)
            const hadFilters = prevCallObj.filters && prevCallObj.filters.enabled && prevCallObj.filters.config;
            const hasFilters = currentCallObj.filters && currentCallObj.filters.enabled && currentCallObj.filters.config;
            const filtersCleared = hadFilters && !hasFilters;

            if (paginationChanged || sortingChanged || filtersCleared) {
              useImmediate = true;
            }
          } catch (e) {
            // If we can't parse previous call, default to immediate
            useImmediate = true;
          }
        }

        const generateFn = useImmediate ? generateReportDataImmediate : generateReportData;
        const data = await generateFn({
          config: report.config,
          page: currentPage,
          limit: pageSize,
          search: undefined,
          sorting,
          viewFilters
        })
        if (data) {
          setReportData(data);
          // Cache the response for future use
          setCachedData(currentCallString, data);
        }

        // Mark initial load as complete after first successful data load
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      } catch (error) {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report_data,
          variant: 'error'
        })
      } finally {
        // Don't clear loading state if force refresh is still active
        if (!isForceRefreshingRef.current) {
          setIsDataLoading(false);
        }
      }
    }

    loadReportData();
  }, [report?.id, currentPage, pageSize, sorting, viewFilters, isInitialLoad, generateReportData, generateReportDataImmediate])

  const handleForceRefresh = async () => {
    if (!report?.config) return;

    // Clear cache on force refresh
    responseCache.current.clear();

    setIsForceRefreshing(true);

    await Promise.all([
      // Use immediate call to force update the data
      (async () => {
        setIsDataLoading(true);
        try {
          const data = await generateReportDataImmediate({
            config: report.config,
            page: currentPage,
            limit: pageSize,
            search: undefined,
            sorting,
            viewFilters,
            forceRefresh: true
          });
          if (data) {
            setReportData(data);
          }
        } catch (error) {
          toast({
            title: L.admin_panel.reports.error,
            description: L.admin_panel.reports.failed_to_load_report_data,
            variant: 'error'
          });
        }
      })(),
      // Minimum 400ms delay for UX
      new Promise(resolve => setTimeout(resolve, 400))
    ]);

    setIsForceRefreshing(false);
    setIsDataLoading(false);
  };

  // Handle pagination changes
  const onPaginationChange = React.useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    const newPage = pagination.pageIndex + 1; // Convert 0-based to 1-based
    const newPageSize = pagination.pageSize;

    // If page size changed, reset to page 1
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    } else if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [currentPage, pageSize]);

  // Handle sorting changes
  const onSortingChange = React.useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
  }, [sorting]);

  const handleExport = () => {
    setExportModalOpen(true)
  }

  // Generate dynamic columns from report data
  const generateColumns = React.useMemo((): ColumnDef<any>[] => {
    if (!reportData || reportData.columns.length === 0 || !report?.config?.columns) return []

    // Create a map of field names to column data for fast lookup
    const reportDataColumnsMap = new Map<string, ReportColumnData>()
    reportData.columns.forEach(col => {
      reportDataColumnsMap.set(col.key, col)
    })

    // Build columns in the order specified by report config
    const orderedColumns: (ReportColumnData)[] = []
    report.config.columns.forEach(configCol => {
      if (configCol.kind === 'Field') {
        // Handle synthetic view applicants field - it's UI-only
        // TODO: when we add more actions, change this to a dropdown
        if (configCol.field === SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME) {
          orderedColumns.push({
            key: SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME,
            label: 'Actions'
          })
        } else {
          // Map synthetic UID field to 'uid' for lookup
          const lookupKey = configCol.field === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : configCol.field
          const columnData = reportDataColumnsMap.get(lookupKey)

          if (columnData) {
            orderedColumns.push(columnData)
          }
        }
      }
    })

    return orderedColumns.map(col => ({
      id: col.key,
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.key !== 'uid' && col.key !== '__synthetic_view_applicants__', // Disable sorting for UID and synthetic columns
      sortingFn: 'auto', // Use auto sorting function
      sortDescFirst: false, // Start with ascending when first clicked
      cell: ({ getValue }: any) => {
        // Handle synthetic view applicants field
        if (col.key === '__synthetic_view_applicants__') {
          return renderViewApplicantsCell(getValue());
        }

        const value = getValue()
        if (value === null || value === undefined) {
          return '--'
        }

        // Use CodeDisplay for UID columns
        if (col.key === 'uid') {
          return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />
        }

        return String(value)
      }
    }))
  }, [reportData, report?.config?.columns])

  // Initialize column order when columns change
  // View Applicants column should not be reorderable - it stays at the end
  const initialColumnOrder = React.useMemo(() => {
    return generateColumns
      .filter((col) => {
        const identifier = 'accessorKey' in col ? String(col.accessorKey) : col.id
        return identifier !== '__synthetic_view_applicants__'
      })
      .map((col) => {
        // Get column identifier from the column def
        const identifier = 'accessorKey' in col ? String(col.accessorKey) : col.id
        return identifier || ''
      })
      .filter(Boolean)
  }, [generateColumns])

  // Initialize column order and visibility from URL or defaults
  React.useEffect(() => {
    if (initialColumnOrder.length > 0) {
      const urlData = getColumnsFromUrl();

      if (urlData && urlData.order && Array.isArray(urlData.order)) {
        // Use URL order if available and valid
        const order = urlData.order;
        const validColumns = order.filter(col => initialColumnOrder.includes(col));
        const missingColumns = initialColumnOrder.filter(col => !order.includes(col));
        setColumnOrder([...validColumns, ...missingColumns]);

        // Set hidden columns from URL
        if (urlData.hidden && Array.isArray(urlData.hidden)) {
          const validHiddenColumns = urlData.hidden.filter(col => initialColumnOrder.includes(col));
          setHiddenColumns(validHiddenColumns);
        }
      } else {
        // Use default order if no URL data
        setColumnOrder(prev => {
          if (prev.length === 0) {
            return initialColumnOrder
          }

          // Keep existing order for columns that still exist, add new ones at the end
          const existingColumns = prev.filter(col => initialColumnOrder.includes(col))
          const newColumns = initialColumnOrder.filter(col => !prev.includes(col))
          return [...existingColumns, ...newColumns]
        });
      }
    }
  }, [initialColumnOrder, getColumnsFromUrl])

  // Handle column order changes
  // View Applicants column is not reorderable, so filter it out if it somehow gets included
  const handleColumnOrderChange = React.useCallback((newOrder: string[]) => {
    const filteredOrder = newOrder.filter(col => col !== '__synthetic_view_applicants__');
    setColumnOrder(filteredOrder);
    updateUrlWithColumns(filteredOrder, hiddenColumns);
  }, [hiddenColumns, updateUrlWithColumns]);

  // Helper function to extract column ID
  const getColumnId = useCallback((col: ColumnDef<any>) => {
    if ('accessorKey' in col && typeof col.accessorKey === 'string') {
      return col.accessorKey;
    }
    if ('id' in col && typeof col.id === 'string') {
      return col.id;
    }
    return '';
  }, []);

  // Render main content with simplified conditional logic
  const renderMainContent = () => {
    // Initial loading state
    if (!reportData && (!hasDataLoadingStarted || isDataLoading)) {
      return <LoadingDataState message={L.admin_panel.reports.loading_report_data} />;
    }

    // No data available
    if (!reportData) {
      return <EmptyDataState message={L.admin_panel.reports.no_data_available} />;
    }

    // Normal data table view
    return (
      <div className="flex flex-col gap-2">
        <DataTable
          enableSearch
          enableColumnVisibility
          columns={generateColumns}
          data={reportData.data}
          loading={showDataLoadingSpinner || isForceRefreshing}
          loadingMessage={L.component_library.data_table.loading}
          showPagination={true}
          pageSize={pageSize}
          resizableColumns={!(showDataLoadingSpinner || isForceRefreshing)}
          enableColumnReordering={!(showDataLoadingSpinner || isForceRefreshing)}
          initialColumnOrder={columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          hiddenCols={hiddenColumns}
          onColumnVisibilityChange={(visibility) => {
            const allColumnIds = generateColumns.map(getColumnId).filter(Boolean);
            const newHiddenColumns = allColumnIds.filter(colId => !visibility[colId]);
            setHiddenColumns(newHiddenColumns);
            updateUrlWithColumns(columnOrder, newHiddenColumns);
          }}
          lastUpdatedTimestamp={reportData.lastUpdated}
          getRowId={(row: any, index: number) => row.uid || `row-${index}-${JSON.stringify(row).slice(0, 50)}`}
          filters={() => (
            availableFieldsForFilters.length > 0 && (
              <FilterPanel
                inline
                {...filterPanelProps}
                onChange={handleViewFilterChange}
                isReloading={isDataLoading}
                fetchFieldValues={fetchSuggestions}
              />
            )
          )}
          activeFilters={viewFilters?.enabled && hasAnyFilters(viewFilters) ? (viewFilters.config?.conditions?.length || 0) : 0}
          headerActionButtons={[
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExpandToggle}
              icon={<Icon
                role="decorative"
                type="solid"
                name={isTableExpanded ? "ArrowsPointingInIcon" : "ArrowsPointingOutIcon"}
              />}
              title={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
              aria-label={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
            />
          ]}
          manualPagination={{
            pageCount: reportData.total > 0 ? Math.ceil(reportData.total / pageSize) : 0,
            pageIndex: Math.max(0, currentPage - 1), // Convert 1-based to 0-based
            totalRows: reportData.total || 0,
            onPaginationChange: onPaginationChange
          }}
          manualSorting={{
            sorting: sorting,
            onSortingChange: onSortingChange
          }}
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <AdminPanelPage
        title={<div className="w-96 h-14"><Skeleton size="auto" /></div>}
        description={<div className="w-64 h-9"><Skeleton size="auto" /></div>}
        breadcrumbs={[
          {
            label: L.admin_panel.nav.reports,
            href: '/admin-panel/reports'
          }
        ]}
        fullWidth={true}
      >
        <div className="space-y-4 w-full h-full">
          <Card className="rounded-md">
            <CardBody>
              <div className="flex flex-col gap-y-8 items-center justify-center h-96 text-gray-500">
                <Spinner className="p-0" />
                <div>{L.admin_panel.reports.loading_report}</div>
              </div>
            </CardBody>
          </Card>
        </div>
      </AdminPanelPage>
    )
  }

  if (!report) {
    return null;
  }

  const isDraft = report.status === 'draft';

  const pageActionButtons = [
    <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
      <Button
        size="sm"
        variant='secondary'
        isDisabled={!report?.config || isForceRefreshing}
        onClick={handleForceRefresh}
        icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
        aria-label={L.admin_panel.payments.refresh_data}
      />
    </Tooltip>,
    <Button
      variant="secondary"
      onClick={() => history.push(`/admin-panel/reports/${reportId}/edit`)}
      icon={<Icon name="PencilIcon" type="solid" role="decorative" />}
    >
      {L.admin_panel.reports.edit_report}
    </Button>,
    ...(!isDraft ? [
      <Button
        variant="secondary"
        onClick={handleExport}
        isDisabled={isExporting(reportId)}
        icon={<Icon name="ArrowDownTrayIcon" type="solid" role="decorative" />}
      >
        {isExporting(reportId) ? L.admin_panel.reports.exporting : L.admin_panel.reports.export_report}
      </Button>
    ] : [])
  ]

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.reports,
      href: '/admin-panel/reports'
    },
    {
      label: report.title
    }
  ];

  return (
    <AdminPanelPage
      title={
        <div className="flex items-center gap-3">
          <span>{report.title}</span>
          {isDraft && (
            <Badge variant="info">
              Draft
            </Badge>
          )}
        </div>
      }
      description={report.description || undefined}
      pageActionButtons={pageActionButtons}
      breadcrumbs={breadcrumbs}
      fullWidth={isTableExpanded}
      keyboardShortcuts={[
        { key: 'E', description: L.admin_panel.reports.edit_report_keyboard_shortcut, modifier: 'meta', callback: () => { history.push(`/admin-panel/reports/${reportId}/edit`) } },
        { key: 'R', description: L.admin_panel.reports.refresh_report, modifier: 'meta', callback: () => { handleForceRefresh() } },
        { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
        {
          displayKey: '↑', key: 'ArrowUp', description: L.admin_panel.reports.go_to_reports, modifier: 'meta', callback: () => { history.push('/admin-panel/reports') }
        },
        { key: 'F', description: L.admin_panel.reports.toggle_filters_panel, modifier: ['meta', 'shift'], callback: () => { filterRef.current?.toggleOpen() } },
        {
          key: ['ArrowLeft', 'ArrowRight'],
          modifier: 'alt',
          description: L.admin_panel.reports.navigate_table_pages,
          callback: (key) => {
            if (key === 'ArrowLeft' && reportData && currentPage > 1) {
              setCurrentPage(currentPage - 1)
            }
            if (key === 'ArrowRight' && reportData && reportData.total > 0) {
              const totalPages = Math.ceil(reportData.total / pageSize)
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1)
              }
            }
          }
        },
      ]}
    >
      <div className="space-y-4 w-full h-full">
        <Card className="rounded-md">
          <CardBody>
            {renderMainContent()}
          </CardBody>
        </Card>
      </div>
      <ReportExportModal
        open={exportModalOpen}
        setOpen={setExportModalOpen}
        report={report}
        viewFilters={viewFilters}
      />
    </AdminPanelPage>
  )
}

export const ReportViewPage = () => {
  const { reportId } = useParams<{ reportId: string }>()

  return (
    <FilterProvider
      enableUrlFilters={true}
    >
      <ReportViewPageInner />
    </FilterProvider>
  )
}